import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../config/app-axios.jsx";
import { getUserInfo } from "../reducers/selectors/selectors.jsx";
import { store } from "../store.jsx";
import { clearUserState } from "../reducers/userReducers.jsx";
import { clearMenuState } from "../reducers/menuReducers.jsx";
import { clearLangCodeState } from "../reducers/langCodeReducers.jsx";

const API = axios.create({
    baseURL : API_BASE_URL,
    headers : {
        'Content-Type' : 'application/json',
    },
    withCredentials : true,
});

// 헤더에 토큰을 담아서 처리하는 부분
API.interceptors.request.use(async (config) => {
    
    const userInfo = getUserInfo(store.getState());
    // ①. 강제로 쿠키 삭제를 통한 로그인 정보가 없는 경우
    const persistInfo = sessionStorage.getItem('persist:login');

    // 세션 저장소에 로그인 정보가 없는 경우
    if (!persistInfo) {
        window.location.href = '/login';
    };

    // 유저 정보가 존재하는 경우
    if (userInfo) {
        const token = userInfo.access_token;

        // 요청 헤더에 토큰을 담아서 전달
        config.headers.Authorization = `Bearer ${token}`;
    };

    return config;
});

// 401 발생시 토큰 만료 확인 후 로그인 페이지로 이동
API.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // ②. 토큰 만료 체크 루틴
            const accessToken = getUserInfo(store.getState())?.access_token;
            
            if (accessToken) {
                const decodedToken            = jwtDecode(accessToken);
                const expirationTimeInSeconds = decodedToken.exp;
                const currentTimeInSeconds    = Math.floor(Date.now() / 1000);

                if (expirationTimeInSeconds < currentTimeInSeconds) {
                    // 토큰이 만료되었을 경우 로그인 페이지로 리디렉션
                    store.dispatch(clearUserState());
                    store.dispatch(clearMenuState());
                    store.dispatch(clearLangCodeState());
                    localStorage.removeItem('langCodeInfo');
                    window.location.href = '/login';
                }
            }
        }

        // 401 이외의 다른 에러는 그대로 반환
        return Promise.reject(error);
    }
);

// 로그인
const fetchLogin = async ({ userId, password }) => {
    try {
        const response = await API.post('/login', {
            userId,
            password
        })

        return response.data.result.content;
    } catch (error) {
        console.error('Login Error:', error);
        throw error;
    }
};

// OTP. ZALO (발급)
const generateOTP = async (user) => {

    const authToken = user.access_token;

    try {
        const response = await API.get('/zalo_001', {
            headers : {
                Authorization : `Bearer ${authToken}`
            }
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error Generate OTP:', error);
        throw error;
    }
};

// OTP. ZALO (유효성검사)
const verifyOTP = async ({ user, otp }) => {

    const authToken = user.access_token;

    try {
        const response = await API.post('/zalo_001', {
            otp           
        }, {
            headers : {
                Authorization : `Bearer ${authToken}`
            }
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error Generate OTP:', error);
        throw error;
    }
};

// 메뉴정보
const fetchMenu = async (user) => {

    const authToken = user.access_token;

    try {
        const response = await API.get('/base/base_001', {
            headers : {
                Authorization : `Bearer ${authToken}`
            }
        })

        return response.data.result;
    } catch (error) {
        console.error('Menu Error:', error);
        throw error;
    }
};

// 프로필 수정
const editProfile = async ({ staffInfo }) => {
    try {
        const response = await API.post('/user/user_003', {
            staffInfo : staffInfo
        })

        return response.data.result;
    } catch (error) {
        console.error('Edit Profile Error:', error);
        throw error;
    }
};

// 대출접수현황 조회
const fetchLoan002UserModalData = async ({name,idCardNumber,phoneNumber}) => {
    try {
        // const response = await API.get('/loan/application', { // spring을 바라볼 때
        const response = await API.get('/loan/loan_002_UserModal', { // Django 서버를 바라볼 때
            params: {
                name,
                idCardNumber,
                phoneNumber
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 고객조회 조회
const fetchLoan002UserModalSelectData = async ({customer_id,loanApplicationId}) => {
    try {
        
        // const response = await API.get('/loan/application', { // spring을 바라볼 때
        const response = await API.get('/loan/loan_002_UserModalSelect', { // Django 서버를 바라볼 때
            params: {
                loanApplicationId,
                customer_id
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청 담보물정보 조회
const fetchLoan00203SelectData = async ({loanApplicationId}) => {
    try {
        
        // const response = await API.get('/loan/application', { // spring을 바라볼 때
        const response = await API.get('/loan/loan_002_03_Select', { // Django 서버를 바라볼 때
            params: {
                loanApplicationId,                
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
// 대출신청 추가첨부파일 조회
const fetchLoan00204SelectData = async ({loanApplicationId}) => {
    try {
        
        // const response = await API.get('/loan/application', { // spring을 바라볼 때
        const response = await API.get('/loan/loan_002_04', { // Django 서버를 바라볼 때
            params: {
                loanApplicationId,                
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청(각종수수료) 조회
const fetchLoan002Data = async ({ loanApplicationId }) => {
    try {
        const response = await API.get('/loan/loan_002_02',{
            params: {loanApplicationId},
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
// 대출신청(각종수수료,ekaqhanf) 조회
const fetchloan002CheckData = async ({ use_loan_application_id }) => {
    try {
        const response = await API.get('/loan/loan_002_check',{
            params: {use_loan_application_id},
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
// 대출신청(각종수수료) 조회
const fetchLoan002StaffData = async () => {
    try {
        const response = await API.get('/loan/loan_002_Staff');  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

const fetchLoan002POSTData = async ({formData}) =>  {
    try {
        const response = await API.post(`/loan/loan_002`,formData,{                
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });  
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청 정보 조회
const fetchLoan00201SelectData = async ({loanApplicationId}) => {
    try {
        
        // const response = await API.get('/loan/application', { // spring을 바라볼 때
        const response = await API.get('/loan/loan_002_01', { // Django 서버를 바라볼 때
            params: {
                loanApplicationId,                
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청(대출정보 등록) 조회
const fetchLoan00201DataPOST = async ({loan_application_id, customer_id, loan_date, execution_type, loan_amount, repayment_method, interest_payment_cycle, interest_payment_cycle_unit, interest, interest_unit, interest_payment_period, number_of_interest, interest_per_day, remark, overdue_interest, overdue_interest_type, overdue_delay_day
}) => {
    try {        
        const response = await API.post('/loan/loan_002_01',{
            loan_application_id,
            customer_id,
            loan_date,
            execution_type,
            loan_amount,
            repayment_method,
            interest_payment_cycle,
            interest_payment_cycle_unit,
            interest,
            interest_unit,
            interest_payment_period,
            number_of_interest,
            interest_per_day,
            remark,
            overdue_interest,
            overdue_interest_type,
            overdue_delay_day,
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청(수수료 정보)등록/수정
const fetchLoan00202DataPOST = async ({ allData }) => {    
    try {
        const response = await API.post('/loan/loan_002_02',{
            allData : allData
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청(수수료 정보) 갱신
const fetchLoan00202Renewal = async ({ loan_application_id }) => {        
    try {        
        const response = await API.post('loan/loan_002_renewal',{
            loan_application_id : loan_application_id
        });  
                
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청 추가첨부파일
const fetchLoan00204DataPOST = async (data) => { 
    const { loan_application_id, formData } = data;
    
    try {
        const response = await API.post('/loan/loan_002_04', {
            loan_application_id, 
            formData
        }, 
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청 담보물정보 보관소조회
// const fetchLoan00203Data = async ({}) => {
const fetchLoan00203Data = async () => {
        try {
        // const response = await API.get('/loan/application', { // spring을 바라볼 때
        const response = await API.get('/loan/loan_002_03', { // Django 서버를 바라볼 때
            params: {},
        });
        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출신청 담보물정보 보관소조회
const fetchLoan00203DataPOST = async ({formData}) => {

    try {        
        // const response = await API.post('/loan/loan_002_03', formData,responseData, {
        const response = await API.post('/loan/loan_002_03', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 결재요청 - 대출신청화면에서 심사요청시 등장하는 승인요청 모달에서 결재요청 기능
const fetchLoan00205DataPOST = async ({ id, selectedData }) => {
    try {        
        const response = await API.post('/loan/loan_002_05', {
            loanApplicationId : id,
            selectedData      : selectedData[0]
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};

// 대출채권생성대기 모달
const fetchLoan00301Data = async () => {
    try {
        const response = await API.get('/loan/loan_003_01');        

        return response.data.result;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
// 대출채권생성대기 모달
const fetchLoan00301DataPOST = async ({ data }) => {
    try {
        const response = await API.post(`/loan/loan_003_01`, {
            data,
        });

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Loan Startbutton :', error);
        throw error;
    }
};

// 대출채권생성대기 모달 입금은행 조회
const fetchLoan00301DataBank = async () => {
    try {
        const response = await API.get('/loan/loan_003_01_bank');          
        
        return response.data.result;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
// 대출채권생성대기 모달출금은행 조회
const fetchLoan00301DataMotherBank = async () => {
    try {
        const response = await API.get('/loan/loan_003_01_Mother_bank');          
        
        return response.data.result;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출채권생성대기 모달출금계좌 조회
const fetchLoan00301DataMotherBankno = async () => {
    try {
        const response = await API.get('/loan/loan_003_01_Mother_bankno');          
        
        return response.data.result;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 대출채권 상환스케줄 
const fetchBond002Data = async ({ loan_amt,day_rate,loan_sdate,days,day_interest_amt,day_interest_amt_yn,loan_pre_interest,loan_count }) => {
    try {
        const response = await API.get('/loan/loan_cals_001', { 
            params: {
                loan_amt : loan_amt,
                day_rate : day_rate,
                loan_sdate : loan_sdate,
                days : days,
                day_interest_amt_yn : day_interest_amt_yn,
                day_interest_amt : day_interest_amt,
                loan_pre_interest : loan_pre_interest,
                loan_count: loan_count,
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// ★★★★★★★★★★★★★★★★★★★★★★★★★★★★★★
// ★★★★★★★★★★ 대출채권 상세조회 ★★★★★★★★★★
// ★★★★★★★★★★        GET        ★★★★★★★★★★
// 대출채권 상세조회 (기본적인 고객 정보 + 탭1. 대출정보)
const fetchBondDetailData = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_001/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail data:', error);
        throw error;
    }
};

// 유저 이미지 정보 불러오기
const fetchDownloadFileData = async ({ fileId }) => {
    try {
        const response = await API.get(`/file/file_001/fileId=${fileId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Download File data:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼1. 이자 수기 수납 (납부내역 조회)
const fetchBondDetailButton001Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_001_01/${loanReceivableId}`, {
            params : {
                // repaymentDate : repaymentDate,
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 001 data:', error);
        throw error;
    }
};
// 대출채권 상세조회 버튼1. 이자 수기 수납 (납부내역 조회)
const fetchBondDetailButton00101Data = async ({ loanReceivableId, repaymentDate, isChecked }) => {
    try {
        const response = await API.get(`/bond/detail_001_01_01/${loanReceivableId}`, {
            params : {
                repaymentDate : repaymentDate,
                isChecked     : isChecked, 
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 001 data:', error);
        throw error;
    }
};
// 대출채권 상세조회 버튼1. 이자 수기 수납 (수수료 금액 조회)
const fetchBondDetailButton00102Data = async ({ loanReceivableId, repaymentDate, isChecked }) => {
    try {
        const response = await API.get(`/bond/detail_001_01_02/${loanReceivableId}`, {
            params : {
                repaymentDate : repaymentDate,
                isChecked     : isChecked, 
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 001 data:', error);
        throw error;
    }
};
// 대출채권 상세조회 탭2. 이자납부내역
const fetchBondDetailTab002Data = async ({ loanReceivableId }) => {

    try {
        const response = await API.get(`/bond/detail_002_02/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 002 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭2. 이자납부내역 - 각종수수료
const fetchBondDetailTab002FeeData = async ({ loanReceivableId, interestSeq }) => {

    try {
        const response = await API.get(`/bond/detail_002_02_01/${loanReceivableId}`, {
            params: {
                interestSeq : interestSeq,
            },
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 002 data:', error);
        throw error;
    }
};
// 대출채권 상세조회 탭2. 이자납부내역 - 저장
const fetchBondDetailTab002SaveData = async ({ loanReceivableId, interestSeq, payDate, OrgAmount , payInterest	, interestDay, overdueDay , overdueAmount , payStatus, allData  
}) => {
    try {
        const response = await API.post(`/bond/detail_002_02_01/${loanReceivableId}`, {            
            interestSeq, 
            payDate, 
            OrgAmount , 
            payInterest	,
            interestDay,  
            overdueDay , 
            overdueAmount,
            payStatus,
            allData, 
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 002 Save data:', error);
        throw error;
    }
};


// 대출채권 상세조회 탭3. 변동내역
const fetchBondDetailTab003Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_002_03/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 003 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭4. 담보자산
const fetchBondDetailTab004Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_002_04/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 004 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭5. 연장내역
const fetchBondDetailTab005Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_002_05/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 005 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭6. 채무내역
const fetchBondDetailTab006Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_002_06/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 006 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭7. 상담내역
const fetchBondDetailTab007Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_002_07/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 007 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭8. History
const fetchBondDetailTab008Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_002_08/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 008 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭8. History -수납내역 상세 조회
const fetchBondDetailTab008feeData = async ({ loanReceivableId, repaymentLogId }) => {
    try {
        const response = await API.get(`/bond/detail_002_08_01/${loanReceivableId}`, {
            params : {
                repaymentLogId : repaymentLogId,
            }
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 008 Fee data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭8. History -수납내역 저장
const fetchBondDetailTab008feeSaveData = async ({ 
    loanReceivableId, repaymentLogId, payOrgAmount, payProvisionalAmount, payPrePayPenalty, payInterestAmount, payOverdueAmount, payCanOverdueAmount, repaymentDate, payLoanAmount, allData 
}) => {
    try {

        // console.log('payPrePayPenalty  ' + payPrePayPenalty)
        const response = await API.post(`/bond/detail_002_08_01/${loanReceivableId}`, {
                repaymentLogId,         
                payOrgAmount,        
                payProvisionalAmount,
                payPrePayPenalty,       
                payInterestAmount,   
                payOverdueAmount,    
                payCanOverdueAmount,
                repaymentDate,
                payLoanAmount, 
                allData, 
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 008 Save data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭9. 첨부파일
const fetchBondDetailTab009Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/file/file_001/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 009 File data:', error);
        throw error;
    }
};

// 대출채권 상세조회 탭9. 첨부파일 다운로드
const fetchBondDetailTab009Download = async ({ downloadUrl }) => {
    try {
        const response = await API.post(`/file/file_001`, {
            downloadUrl
        }, {
            responseType: 'arraybuffer', // 이진 데이터로 받아오도록 설정
        });
        
        return response;
    } catch (error) {
        console.error('Error fetching Bond Detail Tab 009 File Download:', error);
        throw error;
    }
};

// ★★★★★★★★★★        POST        ★★★★★★★★★★
// 대출채권 상세조회 버튼1. 이자 수기 수납 (저장)
const fetchBondDetailButton001Save = async (data) => {
    const { loanReceivableId, childData } = data;

    try {
        const response = await API.post(`/bond/detail_001_01/${loanReceivableId}`, {
            childData,
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 001 save:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼2. 상담내역 (저장)
const fetchBondDetailButton002Save = async (data) => {
    const { loanReceivableId, childData } = data;

    try {
        const response = await API.post(`/bond/detail_001_02/${loanReceivableId}`, {
            childData,
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 002 save:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼3. 납부기한연장 (저장)
const fetchBondDetailButton003Save = async (data) => {
    const { loanReceivableId, childData } = data;

    try {
        const response = await API.post(`/bond/detail_001_03/${loanReceivableId}`, {
            childData,
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 003 save:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼4. 가지급금 (조회)
const fetchBondDetailButton004Data = async ({ loanReceivableId }) => {
    try {
        const response = await API.get(`/bond/detail_001_04/${loanReceivableId}`);

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 004 data:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼4. 가지급금 (저장)
const fetchBondDetailButton004Save = async (data) => {
    const { loanReceivableId, childData } = data;

    try {
        const response = await API.post(`/bond/detail_001_04/${loanReceivableId}`, {
            childData,
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 004 save:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼5. Email (발송)
const fetchBondDetailButton005Save = async (data) => {
    const { childData } = data;

    try {
        const response = await API.post('/email', {
            childData,
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 005 save:', error);
        throw error;
    }
};

// 대출채권 상세조회 버튼6. ZALO (발송)
const fetchBondDetailButton006Save = async (data) => {
    const { loanReceivableId, childData } = data;
    const { template }  = childData;

    let type            = '';

    if (template === 'T05') {
        type = '005'
    } else {
        type = '006'
    }

    try {
        const response = await API.post(`/zalo_${type}`, {
            loanReceivableId,
            childData,
        });

        // 조회 데이터는 result 안 content에 담겨져있다.
        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Bond Detail Button 006 save:', error);
        throw error;
    }
};

// 영업일보
const fetchReport001Data = async ({ workdate , branch_code }) => {
    try {
        const response = await API.get('/daily/daily_001', {
            params : {
                workdate,
                branch_code
            }
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};

const fetchReport002Data = async ({ workdate , branch_code }) => {
    try {
        const response = await API.get('/daily/daily_002', {
            params : {
                workdate,
                branch_code
            }
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};


const fetchReport003Data = async ({ workdate , branch_code }) => {
    try {
        const response = await API.get('/daily/daily_003', {
            params : {
                workdate,
                branch_code
            }
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};
const fetchReport004Data = async ({ workdate , branch_code }) => {
    try {
        const response = await API.get('/daily/daily_004', {
            params : {
                workdate,
                branch_code
            }
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};
const fetchReport005Data = async ({ workdate , branch_code }) => {
    try {
        const response = await API.get('/daily/daily_005', {
            params : {
                workdate,
                branch_code
            }
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};
const fetchReport006Data = async ({ workdate , branch_code }) => {
    try {
        const response = await API.get('/daily/daily_006', {
            params : {
                workdate,
                branch_code
            }
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};


// ★★★★★★★★★★★★★★★★★★★★★★★★★★★★★★
// 모계좌 저장
const fetchAcct001DataPost = async ({ Branch_code,Bank_code,Acct_no,Balance_amount,Remark }) => {
    try {
        const response = await API.post(`/acct/acct_001`,{
            "Branch_code" : Branch_code,
            "Bank_code": Bank_code,
            "Acct_no": Acct_no,
            "Balance_amount": Balance_amount,
            "Remark": Remark,
        }) 

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 결재대기 - 해당 대출접수번호에 따른 결재 상세 상태
const fetchSanc00201Data = async ({ loanApplicationId, approvalInfoId }) => {
    try {
        const response = await API.get(`/sanc/sanc_002_01/${loanApplicationId}`, {
            params: {
                approvalInfoId : approvalInfoId
            },
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 결재정보 모달 - 거절
const fetchSanc00202Data = async ({ loanApplicationId, approvalInfoId, reason }) => {
    try {
        const response = await API.post(`/sanc/sanc_002_02/${loanApplicationId}`, {
                approvalInfoId,
                reason,
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};

// 결재정보 모달 - 결재
const fetchSanc00203Data = async ({ loanApplicationId, approvalInfoId }) => {
    try {
        const response = await API.post(`/sanc/sanc_002_03/${loanApplicationId}`, {
                approvalInfoId,
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 결재선관리
const fetchSanc003Data = async () => {
    try {
        const response = await API.get('/sanc/sanc_003');  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 결재선관리 - 지점사용자
const fetchSanc003UserData = async () => {
    try {
        const response = await API.get('/sanc/sanc_003/01');  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
// 결재선관리 - 결재선사용자
const fetchSanc003ApprovalData = async (approval_line_id) => {
    try {
        const response = await API.get('/sanc/sanc_003/02',{
            approval_line_id : approval_line_id
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 결재선등록
const fetchSanc003ApprovalSave = async ( name, saveData ) => {
    try {
        const response = await API.post('/sanc/sanc_003_01', {
            name,
            saveData
        });  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching Approval Save data:', error);
        throw error;
    }
};

// 아이디 중복체크
const checkUserId = async (userId) => {
    try {
        const response = await API.post('/base/base_003_01', {
            userId
        });

        return response.data;
    } catch (error) {
        console.error('Error Check userId:', error);
        throw error;
    }
};

// 직원관리 저장
const fetchBase003DataPost = async (data) => {
    const { formData } = data;

    try {
        const response = await API.post('/base/base_003', {
            formData
        });  

        return response.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 보관함저장
const fetchProp002DataPost = async ({ storageId,branch_code,setName,Location,Status }) => {
    try {
        const response = await API.post(`/prop/prop_002`,{
            "StorageId"   : storageId,
            "Branch_code" : branch_code,
            "Name"        : setName,
            "Location"    : Location ,   // parseFloat(Fee.replace(/,/g, '')), // 콤마 제거 후 숫자로 변환
            "Status"      : Status,
        }) 

        return response.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 메뉴권한관리
const fetchSettings001Data = async ({authority_id}) => {
    try {
        const response = await API.get('/settings/settings_001',{
            params: {
                authority_id : authority_id
            },
        });

        return response.data.result;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 메뉴 저장
const fetchSettings001DataPost = async ({ responseData,menuCheckStates,authority_id }) => {
    try {
        const response = await API.post('/settings/settings_001', {
            menulist     : responseData,
            menuStates   : menuCheckStates,
            authority_id : authority_id
        });  

        return response.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};


// 메뉴권한관리 > 사용자의 권한정보
const fetchAuthorityOptions = async () => {
    try {
        const response = await API.get('/settings/settings_001/authrityOption');  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching fetchAuthorityOptions data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// 수수료저장
const fetchSettings002DataPost = async (data) => {
    const { formData } = data;

    try {
        const response = await API.post('/settings/settings_002', {
            formData
        });  

        return response.data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};

// 수수료 정보 (수수료관리내 입력시에 사용할 selectbox 용)
const fetchFeeInfoOptions = async () => {
    try {
        const response = await API.get('/settings/settings_002/feeOption');  

        return response.data.result.content;
    } catch (error) {
        console.error('Error fetching FeeInfoOptions data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// SELECT BOX 로 표시할 기준정보 데이터 모음 
// const fetchBaseOptions = async ({biz_code}) => {
const fetchBaseOptions = async () => {
    try {
        const response = await API.get('/base/base_100');
        // // nick_name : 
        // // 상환방법,이자지급주기,이자,이자지급시기,사용자권한,사용자상태,다국어,보관함상태
        return response.data.result.content;
        // return result ;
    } catch (error) {
        console.error('Error fetching fetchBaseOptions data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

//대출실행 입금계좌 예금주조회
const fetchInquiry = async ({ inBank,inBankNo,amount,cardAccGb }) => {    
    try {
        const response = await API.get('/bank/inquiry',{
            params: {
                inBank   : inBank,
                inBankNo : inBankNo,
                amount   : amount,
                cardAccGb: cardAccGb,
            },
        });
        return response.data.result.content;
        // return result ;
    } catch (error) {
        console.error('Error fetching fetchBaseOptions data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};
//대출실행 가상계좌조회
const fetchGetVaAcct = async ({account_number}) => {    
    try {
        const response = await API.get('/loan/va003',{
            params : {
                account_number : account_number,
            }
        });
        return response.data.result.content;
        // return result ;
    } catch (error) {
        console.error('Error fetching fetchBaseOptions data:', error);
        throw error; // Re-throw the error for the calling code to handle
    }
};

// ★★★★★★★★★★★★★★★★★★★★★★★★★★★★★
// ★★★★★★★★★★★ 은행 API 모음 ★★★★★★★★★★★
// 1. 잔액조회 API (=> Account)
const bankGetAccount = async () => {
    try {
        const response = await API.get('/bank/account');

        return response.data.result.content;

    } catch (error) {
        console.error('Error Bank Get Account : ', error);
        throw error;
    }
};

const apiFunctions = {
    fetchLogin,
    generateOTP,
    verifyOTP,
    fetchMenu,
    editProfile,
    fetchLoan002UserModalData,
    fetchLoan002UserModalSelectData,
    fetchLoan002Data,
    fetchloan002CheckData,
    fetchLoan002StaffData,    
    fetchLoan002POSTData,
    fetchLoan00201SelectData,
    fetchLoan00201DataPOST,
    fetchLoan00202DataPOST,
    fetchLoan00202Renewal,
    fetchLoan00203Data,
    fetchLoan00203DataPOST,
    fetchLoan00203SelectData,
    fetchLoan00204SelectData,
    fetchLoan00204DataPOST,
    fetchLoan00205DataPOST,
    fetchLoan00301Data,
    fetchLoan00301DataPOST,
    fetchLoan00301DataBank,
    fetchLoan00301DataMotherBank,
    fetchLoan00301DataMotherBankno,
    fetchReport001Data,
    fetchReport002Data,
    fetchReport003Data,
    fetchReport004Data,
    fetchReport005Data,
    fetchReport006Data,
    fetchInquiry,
    fetchGetVaAcct,
    fetchAcct001DataPost,
    checkUserId,
    fetchBase003DataPost,
    fetchSanc00201Data,
    fetchSanc00202Data,
    fetchSanc00203Data,
    fetchSanc003Data,
    fetchSanc003UserData,
    fetchSanc003ApprovalData,
    fetchSanc003ApprovalSave,
    fetchBond002Data,
    fetchBondDetailData,
    fetchDownloadFileData,
    fetchBondDetailTab002Data,
    fetchBondDetailTab002FeeData,
    fetchBondDetailTab002SaveData,
    fetchBondDetailTab003Data,
    fetchBondDetailTab004Data,
    fetchBondDetailTab005Data,
    fetchBondDetailTab006Data,
    fetchBondDetailTab007Data,
    fetchBondDetailTab008Data,
    fetchBondDetailTab008feeData,
    fetchBondDetailTab008feeSaveData,
    fetchBondDetailTab009Data,
    fetchBondDetailTab009Download,
    fetchBondDetailButton001Data,
    fetchBondDetailButton00101Data,
    fetchBondDetailButton00102Data,
    fetchBondDetailButton001Save,
    fetchBondDetailButton002Save,
    fetchBondDetailButton003Save,
    fetchBondDetailButton004Data,
    fetchBondDetailButton004Save,
    fetchBondDetailButton005Save,
    fetchBondDetailButton006Save,
    fetchProp002DataPost,
    fetchSettings001Data,
    fetchSettings001DataPost,
    fetchAuthorityOptions,
    fetchSettings002DataPost,
    fetchFeeInfoOptions,
    fetchBaseOptions,
    bankGetAccount,
};

export default apiFunctions;